/**
 * @author lifei
 * 功能：全局状态管理
 */

import { defineStore } from 'pinia'
import { Actions } from './actions'

type ActionsType = typeof Actions
type Modules = Exclude<keyof ActionsType, 'prototype'>
type Methods<M extends Modules> = keyof ActionsType[M]

export const useStore = defineStore('store', {
  state: () => {
    return {
      sLoginInfo: null,
      sUserInfo: null,
      sToken: null,
      sTabs: [],
      sTab: null,
      sActiveTab: null,
      sFavorites: [],
      sFavorite: null,
      sTheme: 'default',
      sBusId: null,
      sKeepAliveKeys: [],
    } as TCommon.TStore.State
  },

  getters: {
    /**
     * 功能：当前业务组对象
     */
    sBus: (state: TCommon.TStore.State) => {
      const { sBusId, sUserInfo } = state
      return sUserInfo?.busGroupDtoList.find((item) => item.busId === sBusId)
    },

    /**
     * 功能：当前组织对象
     */
    sUserOrg: (state: TCommon.TStore.State) => {
      const { sUserInfo } = state
      return sUserInfo?.userOrganizationList.find((item) => item.orgId === sUserInfo.user.orgId)
    },

    /**
     * 判断是否为省店
     */
    isProvincialStore() {
      const orgObj = this.sUserOrg as TUser.Login['UserOrganization']

      if (orgObj) {
        return orgObj?.ifProvincialStore == 0
      } else {
        return false
      }
    },
    /**
     * 功能：当前公司对象
     * 兼容老数据
     * @param state
     */
    sUserCompany: (state: TCommon.TStore.State) => {
      const { sUserInfo } = state
      let userOrgInfo: TUser.Login['UserOrganization'] | undefined = sUserInfo?.userOrganizationList.find(
        (item) => item.orgId === sUserInfo.user.orgId
      )
      const orgTreeNamelist: string[] | undefined = userOrgInfo?.orgTreeName.split('/')
      if (userOrgInfo) {
        userOrgInfo['companyName'] = orgTreeNamelist?.[1] || ''
      }
      return userOrgInfo
    },

    /**
     * 功能：当前用户所属业务组列表
     * 兼容老数据
     * @param state
     */
    sUserBusGroupList: (state: TCommon.TStore.State) => {
      const { sUserInfo } = state
      return sUserInfo?.busGroupDtoList.map((item): Login['BusGroupDTO'] & { value: string; label: string } => {
        return {
          ...item,
          value: item.busId,
          label: item.busName,
        }
      })
    },

    /**
     * 功能：当前用户
     * 兼容老数据
     * @param state
     **/
    sUser: (state: TCommon.TStore.State) => {
      return state.sUserInfo?.user
    },
  },

  actions: {
    /**
     * 功能：获取状态
     */
    getState(): TCommon.TStore.State {
      return this.$state
    },
    /**
     * 功能：派发 action, 后续优化，并拆分模块，类型约束较麻烦
     * 类定义需要满足 'login/userInfo' -> login.userInfo
     */
    async dispatch<K extends keyof TCommon.TStore.State>(action: TCommon.TStore.Action, options?: TCommon.TStore.Options) {
      // 不能检查错误暂且不使用
      // const [module, method] = action.type.split('/') as [Modules, Methods<Modules>]
      // const cb = Actions[module]?.[method] as Function
      // const ret = await cb(this.$state, action.payload, options)
      // return Promise.resolve(ret as TCommon.TStore.State[K])

      let ret = null
      switch (action.type) {
        /**
         * 功能：login 相关的操作
         */
        case 'login/userInfo':
          ret = await Actions.login.userInfo(this.$state)
          break
        case 'login/emptyUserInfo':
          ret = await Actions.login.emptyUserInfo(this.$state)
          break
        case 'login/doLogin':
          ret = await Actions.login.doLogin(this.$state, action.payload)
          break
        case 'login/loginInfo':
          ret = await Actions.login.loginInfo(this.$state, action.payload)
          break

        /**
         * 功能：tab 页签相关操作
         */
        case 'tab/add':
          ret = await Actions.tab.add(this.$state, action.payload)
          break
        case 'tab/active':
          ret = await Actions.tab.active(this.$state, action.payload)
          break
        case 'tab/remove':
          ret = await Actions.tab.remove(this.$state, action.payload)
          break
        case 'tab/removeMore':
          ret = await Actions.tab.removeMore(this.$state, action.payload)
          break
        case 'tab/getList':
          ret = await Actions.tab.getList(this.$state)
          break
        case 'tab/updateList':
          ret = await Actions.tab.updateList(this.$state, action.payload)
          break
        case 'tab/getActive':
          ret = await Actions.tab.getActive(this.$state)
          break
        case 'tab/empty':
          ret = await Actions.tab.empty(this.$state)
          break

        /**
         * 功能：favorite 页签收藏 相关操作
         */
        case 'favorite/getList':
          ret = await Actions.favorite.getList(this.$state)
          break
        case 'favorite/add':
          ret = await Actions.favorite.add(this.$state, action.payload)
          break
        case 'favorite/remove':
          ret = await Actions.favorite.remove(this.$state, action.payload)
          break
        case 'favorite/resetList':
          ret = await Actions.favorite.resetList(this.$state, action.payload)
          break
        case 'favorite/empty':
          ret = await Actions.favorite.empty(this.$state)
          break

        /**
         * 功能： oss 相关操作
         */
        case 'oss/uploadFile':
          ret = await Actions.oss.uploadFile()
          break
        case 'oss/getFileUrl':
          ret = await Actions.oss.getFileUrl()
          break

        /**
         * 功能： common 公共模块相关操作
         */
        case 'common/switchBus':
          ret = await Actions.common.switchBus(this.$state, action.payload)
          break
        case 'common/switchTheme':
          ret = await Actions.common.switchTheme(this.$state)
          break
        case 'common/getTheme':
          ret = await Actions.common.getTheme(this.$state)
          break
        case 'common/addKeepAliveKeys':
          ret = await Actions.common.addKeepAliveKeys(this.$state, action.payload)
          break
        case 'common/removeKeepAliveKeys':
          ret = await Actions.common.removeKeepAliveKeys(this.$state, action.payload)
          break
        case 'common/getKeepAliveKeys':
          ret = await Actions.common.getKeepAliveKeys(this.$state, action.payload)
          break

        default: {
          const _exhaustiveCheck: never = action.type
        }
      }
      return Promise.resolve(ret as TCommon.TStore.State[K])
    },
  },
})

export * from './module/productCenter'
export * from './module/productManager'
export * from './module/ossUpload'

export const store = createPinia()

import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

store.use(piniaPluginPersistedstate)
